import { useEffect, useMemo } from "react";
import { useScrollDepth } from "@/lib/scrollTracking";
import { motion } from "framer-motion";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { riseFade } from "@/lib/motion";
import { openRequestAccess } from "@/components/landing/RequestAccessDialog";
import { CTA_VARIANT_COPY, EXPERIMENTS, getVariant, trackExposure } from "@/lib/experiment";
import { trackCtaClick, trackCtaImpression } from "@/lib/observability";
import { useImpression } from "@/lib/useImpression";
import { useCanonical } from "@/lib/useCanonical";


const META_TITLE = "Who we are | Myelina Health";
const META_DESC = "The co-founders behind Myelina Health.";

const setMeta = (name: string, content: string, attr: "name" | "property" = "name") => {
  let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const Initials = ({ initials }: { initials: string }) => (
  <div
    aria-hidden="true"
    className="w-24 h-24 rounded-full bg-secondary border border-border/60 flex items-center justify-center"
  >
    <span className="font-display text-2xl text-foreground tracking-tight">{initials}</span>
  </div>
);

const FounderCard = ({
  initials,
  name,
  role,
  children,
}: {
  initials: string;
  name: string;
  role: string;
  children: React.ReactNode;
}) => (
  <Card className="p-8 sm:p-10 bg-card border-border/60 shadow-sm">
    <div className="flex flex-col items-start gap-6">
      <Initials initials={initials} />
      <div>
        <h2 className="font-display text-2xl sm:text-3xl text-foreground tracking-tight">{name}</h2>
        <p className="text-sm text-muted-foreground mt-1">{role}</p>
      </div>
      <div className="space-y-4 text-[15px] leading-[1.75] text-muted-foreground">{children}</div>
    </div>
  </Card>
);

const WhoWeAre = () => {
  useCanonical({ path: "/who-we-are" });
  useScrollDepth("who_we_are");

  // Sticky A/B assignment for the closing CTA on this page.
  const ctaVariant = useMemo(
    () => getVariant(EXPERIMENTS.whoWeArePrimaryV1, ["A", "B", "C"] as const),
    [],
  );
  const ctaLabel = CTA_VARIANT_COPY[EXPERIMENTS.whoWeArePrimaryV1][ctaVariant];
  const experiments = useMemo(
    () => ({ [EXPERIMENTS.whoWeArePrimaryV1]: ctaVariant }),
    [ctaVariant],
  );

  useEffect(() => {
    trackExposure(EXPERIMENTS.whoWeArePrimaryV1, ctaVariant);
  }, [ctaVariant]);

  const { ref: ctaRef, firstVisibleAtRef } = useImpression<HTMLButtonElement>(() => {
    trackCtaImpression("who_we_are_closing", ctaLabel, {
      experiments,
      variant_label: ctaVariant,
    });
  });

  useEffect(() => {
    document.title = META_TITLE;
    setMeta("description", META_DESC);
    setMeta("og:title", META_TITLE, "property");
    setMeta("og:description", META_DESC, "property");

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.appendChild(canonical);
    }
    canonical.href = "https://myelinahealth.com/who-we-are";

  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-32 pb-24 sm:pt-40 sm:pb-32">
        <div className="container mx-auto px-6 sm:px-10 lg:px-16">
          {/* Header */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-10% 0px" }}
            variants={riseFade}
            className="max-w-3xl mx-auto text-center space-y-6 mb-16 sm:mb-24"
          >
            <div className="eyebrow justify-center mx-auto">Founders</div>
            <h1 className="font-display text-foreground text-display-xl">Who we are.</h1>
            <p className="text-lg sm:text-xl leading-[1.7] text-muted-foreground max-w-2xl mx-auto">
              Myelina Health was built by a woman living with MS and the technologist who
              listens closely alongside her. Lived experience on one side, quiet engineering
              on the other, sharing a roof, and a rhythm, for the women navigating MS with us.
            </p>
          </motion.div>

          {/* Founder cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <FounderCard initials="AO" name="Aaryn Olesen" role="Co-founder">
              <p>
                Aaryn Olesen is the co-founder of Myelina Health. Diagnosed with MS, Aaryn channels
                lived experience into building tools that meet women where they actually are:
                navigating symptoms, careers, relationships, and motherhood, often all at once.
              </p>
              <p>
                Her path to this work spans more than two decades in pharmacovigilance, where
                she dedicated her career to patient safety, most of it at AstraZeneca, growing
                into leadership and executive roles along the way. Aaryn holds a Bachelor's in
                Psychology from UCLA, completed doctoral work at the University of North
                Carolina at Chapel Hill, and earned a Master of Public Health in Health Policy
                and Management from the Yale School of Public Health. She is a National Board
                Certified Health &amp; Wellness Coach trained through the Mayo Clinic and a
                Diplomate of the American Board of Lifestyle Medicine.
              </p>
              <p>
                Aaryn lives in Dana Point, California, where she draws daily energy from being
                near the ocean.
              </p>
            </FounderCard>

            <FounderCard
              initials="BP"
              name="Brian Pentz"
              role="Co-founder &amp; Technical Architect"
            >
              <p>
                Brian Pentz is the co-founder and technical architect of Myelina Health. He is the
                founder of AI Keeps It Real, an applied AI practice that builds production
                multi-agent systems for individuals and small teams.
              </p>
              <p>
                Brian is a Google for Startups alumnus, selected as a founder in the program,
                and a licensed professional with experience across real estate and finance, backgrounds that taught him how to operate in regulated environments where
                details matter and trust is earned slowly. At Myelina Health, he is responsible for
                the privacy-first infrastructure, the multi-model AI stack, and the data
                pipeline connecting Apple Health, Oura Ring, and patient-reported outcomes.
              </p>
              <p>Brian lives in California with his wife and their child.</p>
            </FounderCard>
          </div>

          {/* Pull quote */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-10% 0px" }}
            variants={riseFade}
            className="max-w-3xl mx-auto text-center py-24 sm:py-32"
          >
            <blockquote className="font-display italic text-2xl sm:text-3xl md:text-4xl leading-[1.4] text-foreground tracking-tight">
              &ldquo;Lived experience listening on one side. Engineering quietly on the other.
              Where the two meet, Myelina begins.&rdquo;
            </blockquote>
          </motion.div>

          {/* Closing CTA. A/B tested (who_we_are_primary_cta_v1) */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-10% 0px" }}
            variants={riseFade}
            className="max-w-2xl mx-auto text-center space-y-5 pb-8"
          >
            <p className="text-[15px] leading-[1.7] text-muted-foreground">
              Want to try Myelina alongside us? Access is invite-only for now.
            </p>
            <Button
              ref={ctaRef}
              variant="premium"
              size="lg"
              className="group"
              onClick={() => {
                const msToClick = firstVisibleAtRef.current
                  ? Date.now() - firstVisibleAtRef.current
                  : undefined;
                trackCtaClick("who_we_are_closing", ctaLabel, {
                  destination: "request_access_dialog",
                  experiments: { ...experiments, ms_to_click: String(msToClick ?? "") },
                });
                openRequestAccess("who_we_are_closing");
              }}
            >
              {ctaLabel}
              <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
            </Button>
          </motion.div>

        </div>
      </main>
      <Footer />
    </div>
  );
};

export default WhoWeAre;
